import { lazy } from 'react';

/**
 * React.lazy with a one-shot recovery from stale chunk requests.
 *
 * Route chunks are hashed and served with `max-age=31536000,immutable`, while
 * index.html is `no-cache` and every deploy invalidates `/*`. A user who loaded
 * the page before a deploy therefore holds an index.html that names chunks S3
 * no longer has. Navigating then rejects the dynamic import and, without this,
 * leaves an empty panel behind the error boundary.
 *
 * The recovery is a single reload: the fresh index.html names the chunks that
 * do exist. The attempt is recorded in sessionStorage so a genuinely broken
 * chunk — one missing for any other reason — surfaces to the error boundary on
 * the second pass instead of reloading forever.
 *
 * sessionStorage (not localStorage) so the flag dies with the tab, and every
 * access is guarded: Safari private mode throws on access rather than
 * returning null, and a throw here would mask the real import error.
 */

const RELOAD_KEY = 'route-chunk-reload-attempted';

function readFlag() {
  try {
    return window.sessionStorage.getItem(RELOAD_KEY) === 'true';
  } catch {
    // Storage unavailable — treat as "already tried" so we never reload blindly.
    return true;
  }
}

function writeFlag(value) {
  try {
    if (value) {
      window.sessionStorage.setItem(RELOAD_KEY, 'true');
    } else {
      window.sessionStorage.removeItem(RELOAD_KEY);
    }
  } catch {
    // Non-fatal: worst case we skip the retry on the next stale chunk.
  }
}

/**
 * @param {() => Promise<{ default: React.ComponentType }>} factory
 *   The same `() => import('./Page')` you would hand to React.lazy.
 * @returns {React.LazyExoticComponent}
 */
export default function lazyWithRetry(factory) {
  return lazy(async () => {
    try {
      const module = await factory();
      // A successful load means we are on a consistent build again.
      writeFlag(false);
      return module;
    } catch (error) {
      if (readFlag()) {
        // The reload already happened and the chunk is still missing, so this
        // is a real failure. Let the error boundary show it.
        throw error;
      }

      writeFlag(true);
      window.location.reload();

      // The reload replaces the document; resolving or rejecting here would
      // only race it. Hand back a promise that never settles so Suspense keeps
      // showing the fallback for the moment the page has left.
      return new Promise(() => {});
    }
  });
}
